import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { SITE, PLOTS, BUDGETS } from "../../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  ...PLOTS.map((p) => `${p.size} · ${p.name}`),
  "A built villa on-estate",
  "Undecided — please advise",
];

export default function EnquiryForm({ preselected }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    property_interest: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (preselected) setForm((f) => ({ ...f, property_interest: preselected }));
  }, [preselected]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Please enter your full name.");
      return;
    }
    // Phone: must have at least 6 digits (ignore spaces, dashes, +, brackets)
    const phoneDigits = form.phone.replace(/[^\d]/g, "");
    if (phoneDigits.length < 6) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    // Email is optional, but if user typed something it must look like an email.
    const email = form.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("That email doesn't look right. Please check it or leave it blank.");
      return;
    }
    if (!form.budget) {
      toast.error("Please select your budget range.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: email || null,
        property_interest: form.property_interest || null,
        budget: form.budget || null,
        message: form.message.trim() || null,
      });

      // Fire Google Ads / Analytics conversion events before navigating —
      // gtag will queue and send them even mid-navigation.
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "generate_lead", {
            event_category: "lead",
            event_label: "enquiry-form",
            value: 1,
          });
          if (window.AMARJA_ADS_CONVERSION) {
            window.gtag("event", "conversion", {
              send_to: window.AMARJA_ADS_CONVERSION,
              value: 1.0,
              currency: "INR",
            });
          }
        }
        window.dispatchEvent(new CustomEvent("amarja:lead_submitted"));
      } catch (_e) {
        /* analytics non-critical */
      }

      // Mark submitted (defensive — in case user navigates back)
      setSubmitted(true);
      toast.success("Enquiry received. Redirecting…");

      // Navigate to the dedicated thank-you page (cleaner conversion attribution + UX).
      // Pass the name via location state so the page can greet them.
      navigate("/thank-you", {
        replace: false,
        state: { name: form.name.trim() },
      });
    } catch (err) {
      console.error(err);
      // Surface the actual backend validation message so the user knows what to fix.
      const detail = err?.response?.data?.detail;
      let msg = "Something went wrong. Please try again or call us.";
      if (Array.isArray(detail) && detail.length && detail[0]?.msg) {
        const field = detail[0]?.loc?.[detail[0].loc.length - 1] || "field";
        msg = `Please check the ${field} — ${detail[0].msg}`;
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="relative bg-[#F7F5F0] text-[#1A362D]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[720px]">
        <div className="relative overflow-hidden hidden lg:block">
          <img
            src="/img/site/drone1.jpg"
            alt="Amarja Hills aerial view"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A130F] via-[#0A130F]/50 to-[#0A130F]/15" />
          <div className="absolute bottom-0 left-0 p-12 lg:p-16 text-[#F7F5F0]">
            <h3 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] max-w-md font-light">
              Plots of 1, 2 &amp; up to 5 acres.
              <br />
              <em className="font-normal">Speak to a senior advisor.</em>
            </h3>
            <p className="mt-6 max-w-sm text-[#F7F5F0]/85 leading-relaxed">
              Share your details and a senior advisor will personally walk you
              through the master plan, available plots and pricing.
            </p>
          </div>
        </div>

        <div className="bg-[#F7F5F0] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <h2 className="reveal font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1A362D] leading-[0.98] tracking-tight mb-12 font-light">
            Get the brochure &amp; <br />
            <em className="font-normal">book a site visit.</em>
          </h2>

          {submitted ? (
            <div
              data-testid="enquiry-success"
              className="border border-[#1A362D]/20 p-10 bg-[#EBE7DF]"
            >
              <div className="w-12 h-12 bg-[#1A362D] text-[#F7F5F0] flex items-center justify-center mb-6">
                <Check className="w-6 h-6" />
              </div>
              <h3 className="font-serif-display text-3xl text-[#1A362D] leading-tight font-light">
                Thank you, {form.name.split(" ")[0]}.
              </h3>
              <p className="mt-4 text-[#3A453B]">
                A senior advisor will reach out within 24 hours on{" "}
                <strong className="text-[#1A362D]">{form.phone}</strong>.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  data-testid="success-whatsapp"
                  href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
                    `Hello Amarja Hills, I'm ${form.name}. I just submitted an enquiry.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-dark"
                >
                  Continue on WhatsApp
                </a>
                <a
                  data-testid="success-call"
                  href={`tel:${SITE.telHref}`}
                  className="btn-primary"
                >
                  Call {SITE.phone}
                </a>
              </div>
            </div>
          ) : (
            <form
              data-testid="enquiry-form"
              onSubmit={submit}
              className="space-y-7"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <div>
                  <label className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold block mb-1">
                    Full Name *
                  </label>
                  <input
                    data-testid="enquiry-name"
                    type="text"
                    className="input-line"
                    placeholder="Your name"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold block mb-1">
                    Phone *
                  </label>
                  <input
                    data-testid="enquiry-phone"
                    type="tel"
                    className="input-line"
                    placeholder="+91 98 ..."
                    value={form.phone}
                    onChange={update("phone")}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold block mb-1">
                  Email
                </label>
                <input
                  data-testid="enquiry-email"
                  type="email"
                  className="input-line"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold block mb-1">
                  Interested In
                </label>
                <select
                  data-testid="enquiry-property"
                  className="input-line bg-transparent"
                  value={form.property_interest}
                  onChange={update("property_interest")}
                >
                  <option value="">Select a plot size</option>
                  {form.property_interest &&
                    !OPTIONS.includes(form.property_interest) && (
                      <option value={form.property_interest}>
                        {form.property_interest}
                      </option>
                    )}
                  {OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold block mb-1">
                  Budget *
                </label>
                <select
                  data-testid="enquiry-budget"
                  className="input-line bg-transparent"
                  value={form.budget}
                  onChange={update("budget")}
                  required
                >
                  <option value="">Select your budget range</option>
                  {form.budget && !BUDGETS.includes(form.budget) && (
                    <option value={form.budget}>{form.budget}</option>
                  )}
                  {BUDGETS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold block mb-1">
                  Message
                </label>
                <textarea
                  data-testid="enquiry-message"
                  className="input-line resize-none"
                  rows={3}
                  placeholder="Anything we should know before we call?"
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <button
                  data-testid="enquiry-submit"
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? "Submitting…" : "Submit Enquiry"}
                </button>
                <a
                  data-testid="enquiry-whatsapp"
                  href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
                    "Hello Amarja Hills, I'd like to enquire about buying a plot."
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-dark"
                >
                  Or WhatsApp Us
                </a>
              </div>

              <p className="text-xs text-[#4A524D]/80 leading-relaxed pt-2">
                By submitting, you consent to be contacted by Nanak Properties
                regarding Amarja Hills. We never share your details.
              </p>

              <div
                data-testid="rent-redirect"
                className="mt-4 pt-5 border-t border-[#1A362D]/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <p className="text-sm text-[#4A524D]">
                  Looking to rent a villa instead?
                </p>
                <a
                  data-testid="rent-redirect-link"
                  href={SITE.experienceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#987F53] text-[#987F53] hover:bg-[#987F53] hover:text-[#F7F5F0] text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors self-start sm:self-auto"
                >
                  Visit amarjaexperience.com →
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
